import {
  useCallback,
  useEffect,
  useState,
} from "react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

import PageHeader from "@/components/PageHeader";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Switch } from "@/components/ui/switch";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { Badge } from "@/components/ui/badge";

import {
  Plus,
  Users,
  Pencil,
  Search,
  Trash2,
  KeyRound,
} from "lucide-react";

import { toast } from "sonner";


const COLLABORATOR_ROLES = [
  "Motorista",
  "Abastecedor",
  "Mecânico",
  "Operador de Máquinas",
];

const ADMIN_ROLES = [
  "Gestor",
  ...COLLABORATOR_ROLES,
];

const EMPTY = {
  name: "",
  document: "",
  role: "Motorista",
  active: true,

  hasAccess: false,
  email: "",
  password: "",
};


export default function Collaborators() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    ...EMPTY,
  });

  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const { user } = useAuth();

  const isAdmin =
    user?.role === "admin";

  const availableRoles = isAdmin
    ? ADMIN_ROLES
    : COLLABORATOR_ROLES;


  const load = useCallback(async () => {
    try {
      const query = q
        ? `?q=${encodeURIComponent(q)}`
        : "";

      const { data } = await api.get(
        `/collaborators${query}`
      );

      setList(data || []);
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail ||
            "Não foi possível carregar os colaboradores."
        )
      );
    }
  }, [q]);


  useEffect(() => {
    load();
  }, [load]);


  const openNew = () => {
    setForm({
      ...EMPTY,
    });

    setEditId(null);
    setOpen(true);
  };


  const openEdit = (collaborator) => {
    if (
      collaborator.role === "Gestor" &&
      !isAdmin
    ) {
      toast.error(
        "Somente administradores podem editar gestores."
      );

      return;
    }

    setForm({
      name:
        collaborator.name || "",

      document:
        collaborator.document || "",

      role:
        collaborator.role ||
        "Motorista",

      active:
        collaborator.active !== false,

      hasAccess:
        collaborator.has_access === true,

      email:
        collaborator.email || "",

      // Nunca exibimos a senha atual.
      password: "",
    });

    setEditId(collaborator.id);
    setOpen(true);
  };


  const closeDialog = () => {
    setOpen(false);
    setEditId(null);

    setForm({
      ...EMPTY,
    });
  };


  const save = async () => {
    if (!form.name.trim()) {
      toast.error(
        "Nome é obrigatório"
      );

      return;
    }

    if (
      form.role === "Gestor" &&
      !isAdmin
    ) {
      toast.error(
        "Somente administradores podem cadastrar gestores."
      );

      return;
    }

    if (
      form.hasAccess &&
      !form.email.trim()
    ) {
      toast.error(
        "Email é obrigatório para acesso ao sistema"
      );

      return;
    }

    if (
      form.hasAccess &&
      !editId &&
      !form.password.trim()
    ) {
      toast.error(
        "Senha é obrigatória para novo usuário"
      );

      return;
    }

    setSaving(true);

    try {
      const collaboratorPayload = {
        name:
          form.name.trim(),

        document:
          form.document.trim(),

        role:
          form.role,

        active:
          form.active,
      };

      let collaboratorId =
        editId;

      if (editId) {
        await api.put(
          `/collaborators/${editId}`,
          collaboratorPayload
        );
      } else {
        const { data } =
          await api.post(
            "/collaborators",
            collaboratorPayload
          );

        collaboratorId =
          data.id;
      }

      await api.put(
        `/collaborators/${collaboratorId}/access`,
        {
          enabled:
            form.hasAccess,

          email:
            form.hasAccess
              ? form.email.trim()
              : "",

          password:
            form.hasAccess
              ? form.password
              : "",
        }
      );

      toast.success(
        editId
          ? "Colaborador atualizado"
          : form.role === "Gestor"
          ? "Gestor cadastrado"
          : "Colaborador cadastrado"
      );

      closeDialog();
      await load();
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        )
      );
    } finally {
      setSaving(false);
    }
  };


  const remove = async (
    id,
    name,
    role
  ) => {
    if (
      role === "Gestor" &&
      !isAdmin
    ) {
      toast.error(
        "Somente administradores podem excluir gestores."
      );

      return;
    }

    const confirmed =
      window.confirm(
        `Tem certeza que deseja excluir ${
          role === "Gestor"
            ? "o gestor"
            : "o colaborador"
        } "${name}"?`
      );

    if (!confirmed) {
      return;
    }

    try {
      await api.delete(
        `/collaborators/${id}`
      );

      toast.success(
        role === "Gestor"
          ? "Gestor excluído"
          : "Colaborador excluído"
      );

      await load();
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        )
      );
    }
  };


  const roleDescription =
    form.role === "Gestor"
      ? "Gestores possuem acesso gerencial ao sistema."
      : "Define a função operacional do colaborador.";


  return (
    <div
      className="p-6 max-w-6xl mx-auto"
      data-testid="collaborators-page"
    >
      <PageHeader
        title="Colaboradores"
        subtitle="Gestores, motoristas, operadores, abastecedores e mecânicos"
        actions={
          <Dialog
            open={open}
            onOpenChange={(value) => {
              setOpen(value);

              if (!value) {
                setEditId(null);

                setForm({
                  ...EMPTY,
                });
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                className="gap-2"
                onClick={openNew}
                data-testid="new-collab-btn"
              >
                <Plus className="w-4 h-4" />

                Novo Colaborador
              </Button>
            </DialogTrigger>


            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editId
                    ? "Editar"
                    : "Novo"}{" "}
                  {form.role ===
                  "Gestor"
                    ? "Gestor"
                    : "Colaborador"}
                </DialogTitle>
              </DialogHeader>


              <div className="space-y-3">
                <div>
                  <Label>
                    Nome*
                  </Label>

                  <Input
                    data-testid="collab-name"
                    value={form.name}
                    onChange={(event) =>
                      setForm(
                        (current) => ({
                          ...current,

                          name:
                            event.target
                              .value,
                        })
                      )
                    }
                  />
                </div>


                <div>
                  <Label>
                    CPF / Matrícula
                  </Label>

                  <Input
                    data-testid="collab-doc"
                    value={
                      form.document
                    }
                    onChange={(event) =>
                      setForm(
                        (current) => ({
                          ...current,

                          document:
                            event.target
                              .value,
                        })
                      )
                    }
                  />
                </div>


                <div>
                  <Label>
                    Função
                  </Label>

                  <Select
                    value={form.role}
                    onValueChange={(
                      value
                    ) =>
                      setForm(
                        (current) => ({
                          ...current,

                          role:
                            value,
                        })
                      )
                    }
                  >
                    <SelectTrigger data-testid="collab-role">
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent>
                      {availableRoles.map(
                        (role) => (
                          <SelectItem
                            key={
                              role
                            }
                            value={
                              role
                            }
                          >
                            {role}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>

                  <p className="text-xs text-muted-foreground mt-1">
                    {
                      roleDescription
                    }
                  </p>
                </div>


                <div className="flex items-center justify-between p-3 rounded-md border border-border">
                  <div>
                    <div className="font-semibold text-sm">
                      Ativo
                    </div>

                    <div className="text-xs text-muted-foreground">
                      {form.role ===
                      "Gestor"
                        ? "Gestores inativos permanecem cadastrados, mas não devem ser usados operacionalmente."
                        : "Colaboradores inativos não aparecem em formulários."}
                    </div>
                  </div>

                  <Switch
                    checked={
                      form.active
                    }
                    onCheckedChange={(
                      value
                    ) =>
                      setForm(
                        (current) => ({
                          ...current,

                          active:
                            value,
                        })
                      )
                    }
                    data-testid="collab-active"
                  />
                </div>


                <div className="flex items-center justify-between p-3 rounded-md border border-border">
                  <div>
                    <div className="font-semibold text-sm flex items-center gap-2">
                      <KeyRound className="w-4 h-4 text-primary" />

                      Acesso ao
                      sistema
                    </div>

                    <div className="text-xs text-muted-foreground">
                      {form.role ===
                      "Gestor"
                        ? "Necessário para que o gestor possa acessar o painel."
                        : "Permite que este colaborador faça login."}
                    </div>
                  </div>

                  <Switch
                    checked={
                      form.hasAccess
                    }
                    onCheckedChange={(
                      value
                    ) =>
                      setForm(
                        (current) => ({
                          ...current,

                          hasAccess:
                            value,
                        })
                      )
                    }
                    data-testid="collab-access"
                  />
                </div>


                {form.hasAccess && (
                  <div className="space-y-3 p-3 rounded-md border border-border">
                    <div>
                      <Label>
                        Email*
                      </Label>

                      <Input
                        type="email"
                        value={
                          form.email
                        }
                        onChange={(
                          event
                        ) =>
                          setForm(
                            (
                              current
                            ) => ({
                              ...current,

                              email:
                                event
                                  .target
                                  .value,
                            })
                          )
                        }
                        placeholder="usuario@empresa.com"
                        autoComplete="off"
                      />
                    </div>


                    <div>
                      <Label>
                        {editId
                          ? "Nova senha"
                          : "Senha*"}
                      </Label>

                      <Input
                        type="password"
                        value={
                          form.password
                        }
                        onChange={(
                          event
                        ) =>
                          setForm(
                            (
                              current
                            ) => ({
                              ...current,

                              password:
                                event
                                  .target
                                  .value,
                            })
                          )
                        }
                        placeholder={
                          editId
                            ? "Deixe vazio para manter a senha atual"
                            : "••••••••"
                        }
                        autoComplete="new-password"
                      />

                      {editId && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Deixe em
                          branco para não
                          alterar a senha
                          atual.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>


              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={
                    closeDialog
                  }
                  disabled={saving}
                >
                  Cancelar
                </Button>

                <Button
                  onClick={save}
                  disabled={saving}
                  data-testid="save-collab"
                >
                  {saving
                    ? "Salvando..."
                    : "Salvar"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />


      <Card className="p-4 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />

          <Input
            placeholder="Buscar por nome ou documento..."
            value={q}
            onChange={(event) =>
              setQ(
                event.target.value
              )
            }
            className="pl-9"
            data-testid="collab-search"
          />
        </div>
      </Card>


      <Card className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                Nome
              </TableHead>

              <TableHead>
                Documento
              </TableHead>

              <TableHead>
                Função
              </TableHead>

              <TableHead>
                Acesso
              </TableHead>

              <TableHead>
                Status
              </TableHead>

              <TableHead />
            </TableRow>
          </TableHeader>


          <TableBody>
            {list.map(
              (collaborator) => {
                const isManager =
                  collaborator.role ===
                  "Gestor";

                const canManage =
                  !isManager ||
                  isAdmin;

                return (
                  <TableRow
                    key={
                      collaborator.id
                    }
                    data-testid={`collab-row-${collaborator.id}`}
                  >
                    <TableCell className="font-semibold">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-primary" />

                        {
                          collaborator.name
                        }
                      </div>
                    </TableCell>


                    <TableCell className="font-mono text-xs">
                      {collaborator.document ||
                        "—"}
                    </TableCell>


                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          isManager
                            ? "border-primary/30 text-primary"
                            : ""
                        }
                      >
                        {
                          collaborator.role
                        }
                      </Badge>
                    </TableCell>


                    <TableCell>
                      {collaborator.has_access ? (
                        <div className="space-y-1">
                          <Badge
                            variant="outline"
                            className="text-primary border-primary/30"
                          >
                            Com acesso
                          </Badge>

                          {collaborator.email && (
                            <div className="text-xs text-muted-foreground">
                              {
                                collaborator.email
                              }
                            </div>
                          )}
                        </div>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-muted-foreground"
                        >
                          Sem acesso
                        </Badge>
                      )}
                    </TableCell>


                    <TableCell>
                      {collaborator.active ? (
                        <Badge
                          className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
                          variant="outline"
                        >
                          Ativo
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-muted-foreground"
                        >
                          Inativo
                        </Badge>
                      )}
                    </TableCell>


                    <TableCell>
                      <div className="flex items-center gap-1 justify-end">
                        {canManage && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              openEdit(
                                collaborator
                              )
                            }
                            data-testid={`edit-collab-${collaborator.id}`}
                            title="Editar"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}

                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              remove(
                                collaborator.id,
                                collaborator.name,
                                collaborator.role
                              )
                            }
                            data-testid={`delete-collab-${collaborator.id}`}
                            className="text-destructive hover:text-destructive"
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              }
            )}


            {list.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center text-muted-foreground py-6"
                >
                  Nenhum
                  colaborador
                  encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
